import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { 
  Currency, 
  ExchangeRate, 
  WatchlistItem, 
  RateAlert, 
  UserPreferences,
  CurrencyState,
  ConversionResult
} from '@/types/currency'
import { CurrencyConverter } from '@/services/CurrencyConverter'
import { DEFAULT_PREFERENCES, POPULAR_CURRENCIES } from '@/utils/constants'
import { safeStorage, safeJsonParse } from '@/utils/helpers'

interface CurrencyStore extends CurrencyState {
  // Core converter instance
  converter: CurrencyConverter
  
  // Actions
  initializeApp: () => Promise<void>
  updatePreferences: (preferences: Partial<UserPreferences>) => void
  
  // Currency operations
  convert: (amount: number, from: string, to: string) => Promise<ConversionResult | null>
  getExchangeRate: (from: string, to: string) => Promise<ExchangeRate | null>
  refreshRates: () => Promise<void>
  
  // Watchlist operations
  addToWatchlist: (fromCurrency: string, toCurrency: string, name?: string) => void
  removeFromWatchlist: (id: string) => void
  updateWatchlistItem: (id: string, updates: Partial<WatchlistItem>) => void
  
  // Alert operations
  createAlert: (
    fromCurrency: string,
    toCurrency: string,
    targetRate: number,
    condition: 'above' | 'below' | 'equals'
  ) => RateAlert
  removeAlert: (id: string) => void
  toggleAlert: (id: string) => void
  
  // UI state
  setLoading: (loading: boolean) => void
  addError: (error: any) => void
  clearErrors: () => void
  
  // Background refresh
  startBackgroundRefresh: () => void
  stopBackgroundRefresh: () => void
  
  // Data persistence
  saveToStorage: () => void
  loadFromStorage: () => void
}

export const useCurrencyStore = create<CurrencyStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    currencies: [],
    exchangeRates: new Map(),
    watchlist: [],
    alerts: [],
    preferences: DEFAULT_PREFERENCES,
    isLoading: false,
    lastUpdate: null,
    errors: [],
    converter: new CurrencyConverter(),

    // Initialize the application
    initializeApp: async () => {
      const state = get()
      set({ isLoading: true })
      
      try {
        // Load data from storage
        state.loadFromStorage()
        
        // Initialize with popular currencies for fast startup
        const popularCurrencyObjects: Currency[] = POPULAR_CURRENCIES.map(code => ({
          code,
          name: code,
          symbol: code,
          isActive: true
        }))
        set({ currencies: popularCurrencyObjects })
        
        // Preload exchange rates for all popular currency pairs with USD as base
        const preloadPromises = POPULAR_CURRENCIES.map(async (currency) => {
          if (currency === 'USD') return null
          try {
            const rate = await state.converter.getExchangeRate('USD', currency)
            return [`USD-${currency}`, rate] as [string, ExchangeRate]
          } catch (error) {
            console.warn(`Failed to preload rate for USD-${currency}:`, error)
            return null
          }
        })
        
        // Also preload EUR rates for common pairs
        const eurPreloadPromises = ['GBP', 'JPY', 'CAD', 'AUD'].map(async (currency) => {
          try {
            const rate = await state.converter.getExchangeRate('EUR', currency)
            return [`EUR-${currency}`, rate] as [string, ExchangeRate]
          } catch (error) {
            console.warn(`Failed to preload rate for EUR-${currency}:`, error)
            return null
          }
        })
        
        const allResults = await Promise.all([...preloadPromises, ...eurPreloadPromises])
        const newRates = new Map<string, ExchangeRate>()
        
        allResults.forEach(result => {
          if (result && result[1]) {
            newRates.set(result[0], result[1])
            // Also add reverse rates for instant calculations
            const [from, to] = result[0].split('-')
            newRates.set(`${to}-${from}`, {
              ...result[1],
              fromCurrency: to,
              toCurrency: from,
              rate: 1 / result[1].rate
            })
          }
        })
        
        set({ 
          exchangeRates: newRates,
          isLoading: false,
          lastUpdate: new Date()
        })
        
        // Set up background refresh every 45 seconds
        state.startBackgroundRefresh()
        
        // Load full currency list in background (non-blocking)
        state.converter.getSupportedCurrencies().then(currencies => {
          if (currencies.length > 0) {
            set({ currencies })
          }
        }).catch(error => {
          console.warn('Failed to load currencies from API:', error)
        })
      } catch (error) {
        console.error('Failed to initialize app:', error)
        set({
          isLoading: false,
          errors: [...get().errors, error]
        })
      }
    },

    // Update user preferences
    updatePreferences: (newPreferences) => {
      const state = get()
      const updatedPreferences = { ...state.preferences, ...newPreferences }
      set({ preferences: updatedPreferences })
      state.saveToStorage()
    },

    // Convert currency - now uses cached rates for instant conversion
    convert: async (amount, from, to) => {
      const state = get()
      if (amount <= 0) return null
      
      try {
        // First try to get rate from cache for instant conversion
        const cacheKey = `${from}-${to}`
        const cachedRate = state.exchangeRates.get(cacheKey)
        
        if (cachedRate && Date.now() - cachedRate.timestamp < 300000) { // 5 minutes cache
          // Instant conversion using cached rate
          const convertedAmount = amount * cachedRate.rate
          
          return {
            originalAmount: amount,
            convertedAmount,
            formattedAmount: new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: to,
              minimumFractionDigits: state.preferences.decimalPrecision,
              maximumFractionDigits: state.preferences.decimalPrecision,
            }).format(convertedAmount),
            exchangeRate: cachedRate.rate,
            fromCurrency: { code: from, name: from, symbol: from },
            toCurrency: { code: to, name: to, symbol: to },
            timestamp: new Date(),
            rateSource: cachedRate.source
          }
        }
        
        // If no cached rate, fall back to API (this will also update cache)
        const result = await state.converter.convert(amount, from, to, {
          precision: state.preferences.decimalPrecision
        })
        
        return result
      } catch (error) {
        console.error('Conversion failed:', error)
        state.addError(error as any)
        return null
      }
    },

    // Get exchange rate
    getExchangeRate: async (from, to) => {
      const state = get()
      
      try {
        const rate = await state.converter.getExchangeRate(from, to)
        
        // Update the rates map
        const newRates = new Map(state.exchangeRates)
        newRates.set(`${from}-${to}`, rate)
        set({ exchangeRates: newRates })
        
        return rate
      } catch (error) {
        console.error('Failed to get exchange rate:', error)
        state.addError(error)
        return null
      }
    },

    // Refresh all rates
    refreshRates: async () => {
      const state = get()
      set({ isLoading: true })
      
      try {
        const promises = state.watchlist.map(async (item) => {
          try {
            const rate = await state.converter.getExchangeRate(item.fromCurrency, item.toCurrency)
            return [`${item.fromCurrency}-${item.toCurrency}`, rate] as [string, ExchangeRate]
          } catch (error) {
            console.error(`Failed to refresh rate for ${item.fromCurrency}-${item.toCurrency}:`, error)
            return null
          }
        })
        
        const results = await Promise.all(promises)
        const newRates = new Map(state.exchangeRates)
        
        results.forEach(result => {
          if (result) {
            newRates.set(result[0], result[1])
          }
        })
        
        set({ 
          exchangeRates: newRates,
          lastUpdate: new Date(),
          isLoading: false
        })
      } catch (error) {
        console.error('Failed to refresh rates:', error)
        set({ isLoading: false })
        state.addError(error)
      }
    },

    // Watchlist operations
    addToWatchlist: (fromCurrency, toCurrency, name) => {
      const state = get()
      const newItem: WatchlistItem = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        fromCurrency,
        toCurrency,
        name: name || `${fromCurrency}/${toCurrency}`,
        addedAt: new Date(),
        isStarred: false
      }
      
      // Check if pair already exists
      const exists = state.watchlist.some(
        item => item.fromCurrency === fromCurrency && item.toCurrency === toCurrency
      )
      
      if (!exists) {
        set({ watchlist: [...state.watchlist, newItem] })
        state.converter.addToWatchlist(fromCurrency, toCurrency)
        state.saveToStorage()
      }
    },

    removeFromWatchlist: (id) => {
      const state = get()
      const item = state.watchlist.find(w => w.id === id)
      
      if (item) {
        set({ watchlist: state.watchlist.filter(w => w.id !== id) })
        state.converter.removeFromWatchlist(item.fromCurrency, item.toCurrency)
        state.saveToStorage()
      }
    },

    updateWatchlistItem: (id, updates) => {
      const state = get()
      set({
        watchlist: state.watchlist.map(item =>
          item.id === id ? { ...item, ...updates } : item
        )
      })
      state.saveToStorage()
    },

    // Alert operations
    createAlert: (fromCurrency, toCurrency, targetRate, condition) => {
      const state = get()
      
      const alert = state.converter.setupRateAlert(
        fromCurrency,
        toCurrency,
        targetRate,
        condition
      )
      
      set({ alerts: [...state.alerts, alert] })
      state.saveToStorage()
      
      return alert
    },

    removeAlert: (id) => {
      const state = get()
      set({ alerts: state.alerts.filter(alert => alert.id !== id) })
      state.saveToStorage()
    },

    toggleAlert: (id) => {
      const state = get()
      set({
        alerts: state.alerts.map(alert =>
          alert.id === id ? { ...alert, isActive: !alert.isActive } : alert
        )
      })
      state.saveToStorage()
    },

    // UI state management
    setLoading: (loading) => {
      set({ isLoading: loading })
    },

    addError: (error) => {
      const state = get()
      set({ errors: [...state.errors, error] })
    },

    clearErrors: () => {
      set({ errors: [] })
    },

    // Background refresh functionality
    startBackgroundRefresh: () => {
      const state = get()
      
      // Clear any existing interval
      if ((window as any).refreshInterval) {
        clearInterval((window as any).refreshInterval)
      }
      
      // Set up new interval for background refresh every 45 seconds
      ;(window as any).refreshInterval = setInterval(async () => {
        try {
          // Silently refresh rates for popular currency pairs
          const promises = POPULAR_CURRENCIES.map(async (currency) => {
            if (currency === 'USD') return null
            try {
              const rate = await state.converter.getExchangeRate('USD', currency, { forceRefresh: true })
              return [`USD-${currency}`, rate] as [string, ExchangeRate]
            } catch (error) {
              console.warn(`Background refresh failed for USD-${currency}:`, error)
              return null
            }
          })
          
          const results = await Promise.all(promises)
          const newRates = new Map(state.exchangeRates)
          
          results.forEach(result => {
            if (result && result[1]) {
              newRates.set(result[0], result[1])
              // Also update reverse rates
              const [from, to] = result[0].split('-')
              newRates.set(`${to}-${from}`, {
                ...result[1],
                fromCurrency: to,
                toCurrency: from,
                rate: 1 / result[1].rate
              })
            }
          })
          
          set({ 
            exchangeRates: newRates,
            lastUpdate: new Date()
          })
        } catch (error) {
          console.warn('Background refresh failed:', error)
        }
      }, 45000) // 45 seconds
    },

    stopBackgroundRefresh: () => {
      if ((window as any).refreshInterval) {
        clearInterval((window as any).refreshInterval)
        ;(window as any).refreshInterval = null
      }
    },

    // Data persistence
    saveToStorage: () => {
      const state = get()
      try {
        safeStorage.setItem('currency_preferences', JSON.stringify(state.preferences))
        safeStorage.setItem('currency_watchlist', JSON.stringify(state.watchlist))
        safeStorage.setItem('currency_alerts', JSON.stringify(state.alerts))
      } catch (error) {
        console.error('Failed to save to storage:', error)
      }
    },

    loadFromStorage: () => {
      try {
        const preferences = safeJsonParse(
          safeStorage.getItem('currency_preferences') || '{}',
          DEFAULT_PREFERENCES
        )
        
        const watchlist = safeJsonParse(
          safeStorage.getItem('currency_watchlist') || '[]',
          []
        )
        
        const alerts = safeJsonParse(
          safeStorage.getItem('currency_alerts') || '[]',
          []
        )
        
        set({
          preferences: { ...DEFAULT_PREFERENCES, ...preferences },
          watchlist,
          alerts
        })
      } catch (error) {
        console.error('Failed to load from storage:', error)
      }
    }
  }))
)

// Subscribe to changes and save to storage
useCurrencyStore.subscribe(
  (state) => ({
    preferences: state.preferences,
    watchlist: state.watchlist,
    alerts: state.alerts
  }),
  () => {
    // Debounce saves to avoid excessive storage writes
    clearTimeout((window as any).saveTimeout)
    ;(window as any).saveTimeout = setTimeout(() => {
      useCurrencyStore.getState().saveToStorage()
    }, 1000)
  },
  { equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
)